import { useEffect, useState } from "react";
import { FaWordpress, FaTimes, FaTimesCircle, FaFileInvoiceDollar } from "react-icons/fa";
import SearchIcon from '@mui/icons-material/Search';
import { BACKEND } from "../config/configEnvs";
import axios from "axios";
import LoadingScreen from "../components/ui/temp-screens/LoadingScreen";
import NoDataScreen from "../components/ui/temp-screens/NoDataScreen";
import PaginationItem from "../components/ui/components/Pagination";
import Button from '@mui/material/Button';
import DetailsButton from "../components/ui/components/DetailsButton";
import { toast } from "react-toastify";
import DrawerCotizacionCalendarioPagosDetails from "../components/calendarioPagos/DrawerCotizacionCalendarioPagosDetails";
import { useSelector } from "react-redux";
import { formatDate } from "../utils/rawData/dateValues";
import { ResponsiveBar } from '@nivo/bar';
import { ResponsivePie } from '@nivo/pie';

const HistorialDeudores = () => {

  const user = useSelector((state) => state.auth?.user); // Usuario logeado
  const isAdmin = user.area.toUpperCase() === "ADMINISTRACION" && user.puesto.toUpperCase() !== "ASISTENTE"

  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [brandName, setBrandName] = useState("");

  const [view, setView] = useState("Tabla");

  const [record, setRecord] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [services, setServices] = useState([]);

  const [missingPayments, setMissingPayments] = useState(true);

  const [dataDrawer, setDataDrawer] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(false);

  const [paises, setPaises] = useState([]);
  const [currencyExchanges, setCurrencyExchanges] = useState({})

  // Calcular servicios visibles (que tienen montos)
  const serviciosVisibles = services.filter(s => 
    record.some(cliente => 
      cliente.debts?.some(debt => 
        debt.montosServicios?.some(montoServicio => 
          montoServicio.servicioId?.servicio?.id === s.id && montoServicio.monto > 0
        )
      )
    )
  );

  // Calcular total general de pagos
  const totalPagos = record.reduce((total, cliente) => 
    total + (cliente.debts?.reduce((totalDebt, debt) => {
      const totalMontos = debt.montosServicios?.reduce((totalService, montoServicio) => {
        return totalService + montoServicio.monto;
      }, 0) || 0;
      return totalDebt + totalMontos;
    }, 0) || 0), 0
  );

  // Calcular totales por servicio
  const totalesPorServicio = serviciosVisibles.map(servicio => 
    record.reduce((totalCliente, cliente) => {
      const totalServicioCliente = cliente.debts?.reduce((totalDebt, debt) => {
        const serviceAmount = debt.montosServicios?.reduce((totalService, montoServicio) => {
          if (montoServicio.servicioId?.servicio?.id === servicio.id) {
            return totalService + montoServicio.monto;
          }
          return totalService;
        }, 0) || 0;
        return totalDebt + serviceAmount;
      }, 0) || 0;
      return totalCliente + totalServicioCliente;
    }, 0)
  );

  // Preparar datos para el gráfico de barras - Clientes que más deben
  const datosGraficoClientes = record
    .map(cliente => {
      const totalDeuda = cliente.debts?.reduce((totalDebt, debt) => {
        const totalMontos = debt.montosServicios?.reduce((totalService, montoServicio) => {
          return totalService + montoServicio.monto;
        }, 0) || 0;
        return totalDebt + totalMontos;
      }, 0) || 0;

      return {
        cliente: cliente.nombreComercial,
        deuda: totalDeuda
      };
    })
    .filter(item => item.deuda > 0)
    .sort((a, b) => b.deuda - a.deuda)

  // Preparar datos para el gráfico de barras - Servicios con mayor deuda
  const datosGraficoServicios = serviciosVisibles
    .map(servicio => {
      const totalDeudaServicio = record.reduce((totalCliente, cliente) => {
        const totalServicioCliente = cliente.debts?.reduce((totalDebt, debt) => {
          const serviceAmount = debt.montosServicios?.reduce((totalService, montoServicio) => {
            if (montoServicio.servicioId?.servicio?.id === servicio.id) {
              return totalService + montoServicio.monto;
            }
            return totalService;
          }, 0) || 0;
          return totalDebt + serviceAmount;
        }, 0) || 0;
        return totalCliente + totalServicioCliente;
      }, 0);

      return {
        servicio: servicio.name,
        deuda: totalDeudaServicio
      };
    })
    .filter(item => item.deuda > 0)
    .sort((a, b) => a.deuda - b.deuda)

  // Preparar datos para el gráfico de pie - Deuda por categoría
  const datosGraficoCategorias = serviciosVisibles
    .reduce((categorias, servicio) => {
      const categoria = servicio.categoria || 'Sin categoría';
      
      if (!categorias[categoria]) {
        categorias[categoria] = 0;
      }
      
      const totalDeudaCategoria = record.reduce((totalCliente, cliente) => {
        const totalServicioCliente = cliente.debts?.reduce((totalDebt, debt) => {
          const serviceAmount = debt.montosServicios?.reduce((totalService, montoServicio) => {
            if (montoServicio.servicioId?.servicio?.id === servicio.id) {
              return totalService + montoServicio.monto;
            }
            return totalService;
          }, 0) || 0;
          return totalDebt + serviceAmount;
        }, 0) || 0;
        return totalCliente + totalServicioCliente;
      }, 0);
      
      categorias[categoria] += totalDeudaCategoria;
      return categorias;
    }, {});

  // Convertir a formato para el gráfico de pie
  const datosGraficoCategoriasFormateados = Object.entries(datosGraficoCategorias)
    .filter(([categoria, deuda]) => deuda > 0)
    .map(([categoria, deuda]) => ({
      id: categoria,
      label: categoria,
      value: deuda
    }))
    .sort((a, b) => b.value - a.value);

  const getRecord = async () => {
    try {
      setLoading(true)
      let url = `${BACKEND}/api/getClientesDeudores?page=${page}&limit=${limit}`

      if (missingPayments) {
        url += `&missingPayments=${missingPayments}`
      }
      if (brandName) {
        url += `&brandName=${brandName}`
      }
  
      const response = await axios.get(url)

      setRecord(response.data.docs)
      setTotalDocs(response.data.totalDocs)
      setTotalPages(response.data.totalPages)

    } catch (error) {
      return null
    } finally {
      setLoading(false)
    }
  }

  const getServices = async () => {
    try {
      const response = await axios.get(`${BACKEND}/api/servicios?limit=0`)
      setServices(response.data.docs)
    } catch (error) {
      return null
    }
  }

  const getPaises = async () => {
    try {
      const response = await axios.get(`${BACKEND}/api/paises?limit=0`);
      setPaises(response.data.docs);
    } catch (error) {
      console.error(error);
    }
  }

  const getCurrencyExchange = async () => {
    try {
      const response = await axios.get(`${BACKEND}/api/tiposCambio?limit=0`)
      const currencyExchanges = response.data.docs.reduce((acc, item) => {
        acc[item.moneda] = item.tipoCambio
        return acc
      }, {})
      setCurrencyExchanges(currencyExchanges)
    } catch (error) {
      return false
    }
  }

  const handleSearchClients = () => { // Buscamos clientes y colocamos página 1
    setPage(1)
    getRecord()
  }

  const handleResetSearch = () => { // Resetea la búsqueda
    setBrandName(""); // Cambiar el estado
  };

  useEffect(() => {
    getRecord()
  }, [page, limit, missingPayments])

  useEffect(() => {
    getServices()
    getPaises()
    getCurrencyExchange()
  }, [])

  useEffect(() => { 
    if (brandName === "") {
      getRecord(); 
      setPage(1); 
    }
  }, [brandName]);

  const handleOpenDrawer = async (id) => {
    try {
      const response = await axios.get(`${BACKEND}/api/cotizaciones/${id}`)
      //console.log(response)
      setDataDrawer(response.data)
      setOpenDrawer(true)
    } catch (error) {
      toast.error("Error al obtener la cotización")
      console.log(error)
    }
  } 

  return (
    <div className="flex flex-col h-screen w-full">

      <DrawerCotizacionCalendarioPagosDetails
        onClose={() => setOpenDrawer(false)}
        open={openDrawer}
        dataDrawer={dataDrawer}
        refetch={getRecord}
        isAdmin={isAdmin}
        paises={paises}
        currencyExchanges={currencyExchanges}
      />

      {/* NavBar */}
      <div className="flex flex-row px-4 py-2 bg-gray-100">
        <div className="flex flex-row w-full items-center gap-4 justify-between overflow-x-auto overflow-y-hidden px-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          
          <div className="flex items-center gap-4 py-4">
            <FaFileInvoiceDollar className="text-zinc-700 text-2xl" />
            <h2 className="text-2xl font-bold text-zinc-700">Historial de deudores</h2>
          </div>

          <div className="flex flex-row gap-4 items-center justify-center">

            <div className="flex flex-row text-xs text-gray-700 items-center justify-center gap-4">
              <span>Vista:</span>
              <select
                value={view}
                onChange={(e) => setView(e.target.value)}
                className="text-gray-700 hover:cursor-pointer text-xs bg-white border border-gray-300 rounded-lg shadow focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent py-[6px] pr-10"
              >
                <option value="Tabla">Tabla</option>
                <option value="Gráfico">Gráfico</option>
              </select>
            </div>

            <div className="flex flex-row text-xs text-gray-700 items-center justify-center gap-4">
              <span>Límite:</span>
              <select
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
                className="text-gray-700 hover:cursor-pointer text-xs bg-white border border-gray-300 rounded-lg shadow focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent py-[6px] pr-10"
              >
                <option value="30">30</option>
                <option value="40">40</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
              </select>
            </div>

            <div className="relative w-full">
              <input
                type="text"
                placeholder="Buscar marca"
                className="w-[16rem] text-xs text-gray-700 bg-white border border-gray-300 rounded-lg shadow focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent py-[6px] pr-10"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
              />
              {brandName && (
                <FaTimes
                  onClick={() => handleResetSearch()}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 cursor-pointer"
                />
              )}
            </div>

            <div>
              <Button
                variant="contained"
                endIcon={<SearchIcon />}
                sx={{
                  fontSize: "12px",
                  fontWeight: "bold",
                  fontFamily: "poppins",
                  padding: "4px 24px",
                  backgroundColor: "#ff6400",
                  transform: "all",
                  animationDuration: "1000",
                  textTransform: "none",
                  borderRadius: "8px",
                  '&:hover': {
                    backgroundColor: "#bb4a03",
                  }
                }}
                onClick={() => handleSearchClients()}
              >
                Buscar
              </Button>
            </div>

          </div>

        </div>
      </div>

      {/* Body */}
      <div className="flex-grow overflow-y-auto mt-0.5">
        {loading ? (
            <LoadingScreen />
          ) : (
            record.length === 0 ? (
              <NoDataScreen />
            ) : (
              view === "Tabla" ? (
                <table className="w-full text-xs">
                  <thead className="text-xs sticky top-0 text-gray-900 bg-gray-200 z-20">
                    <tr className="text-gray-700 text-center text-xs font-bold">
                      <td className="sticky left-0 w-[6.5rem] max-w-[6.5rem] px-2 py-3 text-center bg-[#FE6400] border-r border-r-white text-white border-red z-20">
                        Cotización(es)
                      </td>
                      <td className="sticky left-[6.5rem] w-[15rem] max-w-[15rem] px-2 py-3 text-center bg-[#FE6400] border-r border-r-white text-white border-red z-20">
                        Cliente
                      </td>
                      <td className="sticky left-[21.5rem] w-[12rem] max-w-[12rem] px-2 py-3 text-center bg-[#FE6400] border-r border-r-white text-white border-red z-20">
                        Total
                      </td>
                      {serviciosVisibles.map((s, i) => {
                        return (
                        <td key={i} className="px-2 py-3 text-center bg-[#FE6400] border-r border-r-white text-white border-red">
                          {s.name}
                        </td>
                        )
                      })}
                    </tr>
                    {/* Fila de totales */}
                      <tr className="bg-gray-100 font-bold text-xs text-center">
                      <td className="sticky left-0 w-[6.5rem] max-w-[6.5rem] py-2 bg-gray-100 z-20">Totales</td>
                      <td className="sticky left-[6.5rem] w-[15rem] max-w-[15rem] py-2 bg-gray-100 z-20"></td>
                      <td className="sticky left-[21.5rem] w-[12rem] max-w-[12rem] text-black py-2 whitespace-nowrap px-2 bg-gray-100 z-20">
                        {totalPagos > 0 ? "S/. " + totalPagos.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "-"}
                      </td>
                      {totalesPorServicio.map((total, i) => (
                        <td key={i} className="text-black py-2 whitespace-nowrap px-2">
                          {total > 0 ? "S/. " + total.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "-"}
                        </td>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {record?.map((e) => {
                    // {record.sort((a, b) => b.debts.reduce((acc, p) => acc + p.precio, 0) - a.debts.reduce((acc, p) => acc + p.precio, 0)).map((e) => {

                      const quotes = [...new Set(e.debts?.map((d) => d.cotizacion.id))];

                      return (
                        <tr className="text-xs py-2 w-full hover:bg-gray-100 transition-all duration-300 group">
                          <td scope="row" className="sticky left-0 w-[6.5rem] max-w-[6.5rem] px-2 py-1 border font-bold border-gray-300 truncate text-start bg-white z-10 group-hover:bg-gray-100">
                            <div className="flex flex-row items-center justify-center gap-2">
                              {quotes.map((q, i) => (
                                <DetailsButton
                                  key={i}
                                  content={q}
                                  action={() => handleOpenDrawer(q)}
                                />
                              ))}
                            </div>
                          </td>
                          <td scope="row" className="sticky left-[6.5rem] w-[15rem] max-w-[15rem] px-2 py-1 border font-bold border-gray-300 truncate text-start bg-white z-10 group-hover:bg-gray-100">
                            {e.nombreComercial}
                          </td>
                          <td scope="row" className="sticky left-[21.5rem] w-[12rem] max-w-[12rem] px-2 py-1 border border-gray-300 text-center bg-white z-10 group-hover:bg-gray-100">
                            {e.debts?.reduce((totalDebt, debt) => {
                              const totalMontos = debt.montosServicios?.reduce((totalService, montoServicio) => {
                                return totalService + montoServicio.monto;
                              }, 0) || 0;
                              return totalDebt + totalMontos;
                            }, 0).toLocaleString('es-PE', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </td>
                          
                          {serviciosVisibles.map((s, i) => {
                            const totalServiceAmount = e.debts?.reduce((totalDebt, debt) => {
                              const serviceAmount = debt.montosServicios?.reduce((totalService, montoServicio) => {
                                // Debug: mostrar los IDs que estamos comparando
                                // console.log(`Comparando: servicioId=${montoServicio.servicioId?.servicio?.id} vs s.id=${s.id}`);
                                // console.log(`Monto: ${montoServicio.monto}`);
                                
                                if (montoServicio.servicioId?.servicio?.id === s.id) {
                                  return totalService + montoServicio.monto;
                                }
                                return totalService;
                              }, 0) || 0;
                              return totalDebt + serviceAmount;
                            }, 0) || 0;

                            // Debug log
                            // console.log(`Cliente: ${e.nombreComercial}, Servicio: ${s.name} (ID: ${s.id}), Total: ${totalServiceAmount}`);
                            
                            return (
                              <td key={i} className={`px-2 py-1 max-w-xs border border-gray-300 truncate w-full ${totalServiceAmount === 0 ? "text-gray-200" : "text-black"}`}>
                                {totalServiceAmount > 0 ? (
                                  <span className="font-bold text-start">Total: {totalServiceAmount.toLocaleString('es-PE', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                ) : (
                                  <span className="text-gray-200 w-full text-center">-</span>
                                )}
                                  <ul className="list-disc pl-4">
                                  {e.debts?.map((d) => {
                                    if (d.montosServicios?.some(montoServicio => montoServicio.servicioId?.servicio?.id === s.id)) {
                                      // Calcular días de atraso
                                      const fechaLimite = new Date(d.fechaLimite);
                                      const fechaActual = new Date();
                                      const diasAtraso = Math.ceil((fechaLimite - fechaActual) / (1000 * 60 * 60 * 24));
                                      
                                      return (
                                        <li key={d.id} className="">
                                          <span className="text-black mr-2">{(d.montosServicios.find(montoServicio => montoServicio.servicioId?.servicio?.id === s.id).monto).toLocaleString('es-PE', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>  
                                          <span className="text-gray-400 text-xs mr-2"> | </span>
                                          <span className={`text-xs mr-2 ${diasAtraso < 0 ? 'text-red-600 font-bold' : diasAtraso === 0 ? 'text-yellow-400 font-bold' : 'text-green-600'}`}>
                                            -{diasAtraso < 0 ? `${Math.abs(diasAtraso)}` : diasAtraso === 0 ? 'Vence hoy' : `${diasAtraso}`}
                                          </span>
                                          <span className="text-gray-400 text-xs mr-2"> | </span>
                                          <span className="text-black text-xs">{formatDate(d.fechaLimite.split("T")[0])}</span>
                                        </li>
                                      )
                                    }
                                  })}
                                </ul>
                              </td>
                            )
                          })}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="flex flex-col gap-4 w-full">

                  <div className="w-full rounded-lg">
                    <h1 className="text-sm font-bold text-gray-800 text-start px-6 pt-6">
                      Deuda de clientes
                    </h1>
                    <div className="w-full h-[600px] rounded-lg px-4">
                      {datosGraficoClientes.length > 0 ? (
                        <ResponsiveBar
                          data={datosGraficoClientes}
                          keys={['deuda']}
                          indexBy="cliente"
                          margin={{ top: 50, right: 130, bottom: 150, left: 100 }}
                          padding={0.3}
                          valueScale={{ type: 'linear' }}
                          indexScale={{ type: 'band', round: true }}
                          colors={{ scheme: 'paired' }}
                          borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                          axisTop={null}
                          axisRight={null}
                          axisBottom={{
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: -20,
                            legend: 'Clientes',
                            legendPosition: 'middle',
                            legendOffset: 100,
                            truncateTickAt: 0
                          }}
                          axisLeft={{
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: 0,
                            legend: 'Deuda Total (S/.)',
                            legendPosition: 'middle',
                            legendOffset: -90,
                            format: value => `S/. ${Number(value).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          }}
                          labelSkipWidth={12}
                          labelSkipHeight={12}
                          labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                          legends={[
                            {
                              dataFrom: 'keys',
                              anchor: 'bottom-right',
                              direction: 'column',
                              justify: false,
                              translateX: 120,
                              translateY: 0,
                              itemsSpacing: 2,
                              itemWidth: 100,
                              itemHeight: 20,
                              itemDirection: 'left-to-right',
                              itemOpacity: 0.85,
                              symbolSize: 20,
                              effects: [
                                {
                                  on: 'hover',
                                  style: {
                                    itemOpacity: 1
                                  }
                                }
                              ]
                            }
                          ]}
                          tooltip={({ data }) => (
                            <div className="bg-white p-3 border border-gray-300 rounded-lg shadow-lg">
                              <div className="font-bold text-gray-800">{data.cliente}</div>
                              <div className="text-gray-600">
                                Deuda: S/. {data.deuda.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </div>
                            </div>
                          )}
                          animate={true}
                          motionStiffness={90}
                          motionDamping={15}
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full">
                          <p className="text-gray-500 text-lg">No hay datos de deudas para mostrar</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="w-full rounded-lg">
                    <h1 className="text-sm font-bold text-gray-800 text-start px-6 pt-6">
                      Deuda por servicio
                    </h1>
                    <div className="w-full h-[600px] rounded-lg px-4">
                        {datosGraficoServicios.length > 0 ? (
                        <ResponsiveBar
                          data={datosGraficoServicios}
                          keys={['deuda']}
                          indexBy="servicio"
                          layout="horizontal"
                          margin={{ top: 50, right: 130, bottom: 150, left: 170 }}
                          padding={0.3}
                          valueScale={{ type: 'linear' }}
                          indexScale={{ type: 'band', round: true }}
                          colors={{ scheme: 'accent' }}
                          borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                          axisTop={null}
                          axisRight={null}
                          axisBottom={{
                          tickSize: 5,
                          tickPadding: 5,
                          tickRotation: 0,
                          legend: 'Deuda Total (S/.)',
                          legendPosition: 'middle',
                          legendOffset: 50,
                          format: value => `S/. ${Number(value).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          }}
                          axisLeft={{
                          tickSize: 5,
                          tickPadding: 5,
                          tickRotation: 0,
                          legend: 'Servicios',
                          legendPosition: 'middle',
                          legendOffset: -150,
                          truncateTickAt: 0
                          }}
                          labelSkipWidth={12}
                          labelSkipHeight={12}
                          labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                          legends={[
                          {
                            dataFrom: 'keys',
                            anchor: 'bottom-right',
                            direction: 'column',
                            justify: false,
                            translateX: 120,
                            translateY: 0,
                            itemsSpacing: 2,
                            itemWidth: 100,
                            itemHeight: 20,
                            itemDirection: 'left-to-right',
                            itemOpacity: 0.85,
                            symbolSize: 20,
                            effects: [
                              {
                                on: 'hover',
                                style: {
                                  itemOpacity: 1
                                }
                              }
                            ]
                          }
                          ]}
                          tooltip={({ data }) => (
                            <div className="bg-white p-3 border border-gray-300 rounded-lg shadow-lg">
                              <div className="font-bold text-gray-800">{data.servicio}</div>
                              <div className="text-gray-600">
                                Deuda: S/. {data.deuda.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </div>
                            </div>
                          )}
                          animate={true}
                          motionStiffness={90}
                          motionDamping={15}
                        />
                        ) : (
                        <div className="flex items-center justify-center h-full">
                          <p className="text-gray-500 text-lg">No hay datos de deudas por servicio para mostrar</p>
                        </div>
                        )}
                    </div>
                  </div>

                  <div className="w-full rounded-lg">
                    <h1 className="text-sm font-bold text-gray-800 text-start px-6 pt-6">
                      Deuda por categoría
                    </h1>
                      <div className="w-full h-[600px] rounded-lg px-4">
                        {datosGraficoCategoriasFormateados.length > 0 ? (
                        <ResponsivePie
                          data={datosGraficoCategoriasFormateados}
                          margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
                          innerRadius={0.5}
                          padAngle={0.7}
                          cornerRadius={3}
                          activeOuterRadiusOffset={8}
                          borderWidth={1}
                          borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                          arcLinkLabelsSkipAngle={10}
                          arcLinkLabelsTextColor="#333333"
                          arcLinkLabelsThickness={2}
                          arcLinkLabelsColor={{ from: 'color' }}
                          arcLabelsSkipAngle={10}
                          arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                          colors={{ scheme: 'paired' }}
                          legends={[
                            {
                              anchor: 'right',
                              direction: 'column',
                              justify: false,
                              translateX: 0,
                              translateY: 80,
                              itemsSpacing: 10,
                              itemWidth: 100,
                              itemHeight: 18,
                              itemTextColor: '#999',
                              itemDirection: 'left-to-right',
                              itemOpacity: 1,
                              symbolSize: 18,
                              symbolShape: 'circle',
                              effects: [
                                {
                                  on: 'hover',
                                  style: {
                                    itemTextColor: '#000'
                                  }
                                }
                              ]
                            }
                          ]}
                          tooltip={({ datum }) => (
                            <div className="bg-white p-3 border border-gray-300 rounded-lg shadow-lg">
                              <div className="font-bold text-gray-800">{datum.label}</div>
                              <div className="text-gray-600">
                                Deuda: S/. {datum.value.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </div>
                              <div className="text-gray-500 text-sm">
                                Porcentaje: {((datum.value / datosGraficoCategoriasFormateados.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(1)}%
                              </div>
                            </div>
                          )}
                          animate={true}
                          motionStiffness={90}
                          motionDamping={15}
                        />
                        ) : (
                        <div className="flex items-center justify-center h-full">
                          <p className="text-gray-500 text-lg">No hay datos de deudas por categoría para mostrar</p>
                        </div>
                        )}
                    </div>
                  </div>
                </div>
              )
            )
          )
        }
      </div>

      {/* Paginador */}
      {view === "Tabla" && (
        <PaginationItem
          actualDocs={record.length}
          totalDocs={totalDocs}
          entity={"deudores"}
          totalPages={totalPages}
          actualPage={page}
          handlePageChange={(event, value) => setPage(value)}
        />
      )}
    </div>
  )
}

export default HistorialDeudores;  